import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Wrench, Mail } from 'lucide-react';

const GOLD_INK = '#8F650F';
const GOLD = '#D89232';
const TERRA = '#E07A5F';
const CREAM = '#F5F0EA';
const ESPRESSO = '#2B2118';

const SectionLabel = ({ children }) => (
  <p className="font-mono text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.26em', color: GOLD_INK }}>
    {children}
  </p>
);

const AccessibilityPage = () => {
  useEffect(() => {
    document.title = 'Erklärung zur Barrierefreiheit · Dickies-Campus';
  }, []);

  const done = [
    ['Lesbare Kontraste', 'Alle Beschriftungen erfüllen die Kontrastanforderungen der WCAG 2.1 AA (mind. 4,5:1).'],
    ['Vollständige Tastaturbedienung', 'Alle Funktionen sind ohne Maus nutzbar — mit sichtbarem Fokusrahmen und ESC-Taste zum Schließen von Dialogen.'],
    ['Screenreader-Unterstützung', 'Deutsche Seitensprache, aussagekräftige Seitentitel, Alternativtexte für Bilder und korrekt ausgezeichnete Dialoge.'],
    ['Sprung zum Inhalt', 'Ein Skip-Link führt Tastatur-Nutzer direkt zum Hauptinhalt jeder Seite.'],
    ['Übungen ohne Ziehen', 'Interaktive Zuordnungs- und Fallübungen sind vollständig per Klick bzw. Tastatur lösbar — kein Drag & Drop erforderlich.'],
    ['Keine Zeitbegrenzung', 'Lerninhalte, Aufgaben und Wissenstests stehen ohne Zeitdruck zur Verfügung.'],
  ];

  const inProgress = [
    ['Untertitel für Lernvideos', 'Videolektionen erhalten schrittweise Untertitel bzw. begleitende Textfassungen.'],
    ['Barrierefreie PDF-Dokumente', 'Kursskripte und Zertifikate sind derzeit nicht für Screenreader optimiert — die Inhalte stehen jedoch vollständig in der barrierearmen Web-Ansicht zur Verfügung.'],
    ['Reduzierte Animationen', 'Die Systemeinstellung „Bewegung reduzieren" wird künftig plattformweit berücksichtigt.'],
  ];

  return (
    <div className="min-h-screen px-4 py-14" style={{ backgroundColor: CREAM }} data-testid="accessibility-page">
      <div className="max-w-2xl mx-auto">
        {/* Masthead */}
        <div className="text-center">
          <h1 className="font-display text-3xl sm:text-4xl" style={{ color: ESPRESSO, letterSpacing: '0.04em' }}>
            DICKIES-CAMPUS
          </h1>
          <p className="font-mono text-[10px] uppercase font-semibold mt-2" style={{ letterSpacing: '0.3em', color: GOLD_INK }}>
            Akademie für Pflege · Erklärung zur Barrierefreiheit
          </p>
          <div className="mx-auto mt-4" style={{ width: 48, height: 1, backgroundColor: GOLD }} />
        </div>

        {/* Einleitung */}
        <div className="mt-10">
          <p className="font-display text-[22px] leading-relaxed" style={{ color: ESPRESSO }}>
            Weiterbildung in der Pflege muss für <span className="italic" style={{ color: TERRA }}>alle</span> zugänglich sein.
          </p>
          <p className="text-[15px] leading-relaxed mt-4 text-sand-700">
            Die Dickies-Campus GmbH gestaltet die Lernplattform <strong>dickies-helden.de</strong> nach
            den international anerkannten <strong>Web Content Accessibility Guidelines (WCAG) 2.1, Stufe AA</strong> —
            in Anlehnung an die Anforderungen des Barrierefreiheitsstärkungsgesetzes (BFSG).
            Diese Erklärung beschreibt den aktuellen Stand und ist Teil unserer freiwilligen Selbstverpflichtung
            zu inklusiver Pflegebildung.
          </p>
        </div>

        {/* Stand der Vereinbarkeit */}
        <div className="mt-10 bg-cream-100 shadow-soft" style={{ border: '1px solid rgba(216,146,50,0.4)', padding: 8 }}>
          <div className="px-7 py-6" style={{ border: '1px solid rgba(216,146,50,0.4)' }}>
            <SectionLabel>Stand der Vereinbarkeit</SectionLabel>
            <p className="font-display text-[19px] mt-2" style={{ color: ESPRESSO }} data-testid="conformance-status">
              Teilweise konform mit WCAG 2.1 AA
            </p>
            <p className="text-[14px] leading-relaxed mt-2 text-sand-700">
              Die zentralen Lernwege — Anmeldung, Kapitel lesen, interaktive Übungen, Wissenstests und
              Zertifikatsabruf — sind barrierefrei nutzbar. An den unten aufgeführten Punkten arbeiten wir aktiv.
            </p>
            <p className="font-mono text-[10px] uppercase mt-4 text-sand-600" style={{ letterSpacing: '0.18em' }}>
              Selbstbewertung · zuletzt geprüft im Juni 2026
            </p>
          </div>
        </div>

        {/* Umgesetzt */}
        <div className="mt-12">
          <SectionLabel>Was bereits umgesetzt ist</SectionLabel>
          <div className="mt-4 space-y-4">
            {done.map(([title, text]) => (
              <div key={title} className="flex gap-3">
                <CheckCircle2 className="w-[18px] h-[18px] flex-shrink-0 mt-0.5" style={{ color: GOLD_INK }} aria-hidden />
                <div>
                  <p className="font-semibold text-[15px]" style={{ color: ESPRESSO }}>{title}</p>
                  <p className="text-[14px] leading-relaxed text-sand-700 mt-0.5">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* In Arbeit */}
        <div className="mt-12">
          <SectionLabel>Woran wir arbeiten</SectionLabel>
          <div className="mt-4 space-y-4">
            {inProgress.map(([title, text]) => (
              <div key={title} className="flex gap-3">
                <Wrench className="w-[18px] h-[18px] flex-shrink-0 mt-0.5" style={{ color: TERRA }} aria-hidden />
                <div>
                  <p className="font-semibold text-[15px]" style={{ color: ESPRESSO }}>{title}</p>
                  <p className="text-[14px] leading-relaxed text-sand-700 mt-0.5">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Feedback */}
        <div className="mt-12 bg-cream-100 shadow-soft" style={{ border: '1px solid rgba(216,146,50,0.4)', padding: 8 }}>
          <div className="px-7 py-6" style={{ border: '1px solid rgba(216,146,50,0.4)' }}>
            <SectionLabel>Barriere gefunden?</SectionLabel>
            <p className="text-[14.5px] leading-relaxed mt-3 text-sand-700">
              Du bist auf ein Hindernis gestoßen oder benötigst Inhalte in einer anderen Form?
              Schreib uns — wir melden uns in der Regel innerhalb von zwei Werktagen.
            </p>
            <a
              href="mailto:info@dickies-helden.de?subject=Barrierefreiheit"
              className="mt-4 inline-flex items-center gap-2 text-[13.5px] font-semibold hover:underline underline-offset-4"
              style={{ color: TERRA }}
              data-testid="accessibility-contact-link"
            >
              <Mail className="w-4 h-4" aria-hidden />
              info@dickies-helden.de
            </a>
          </div>
        </div>

        {/* Fußzeile */}
        <div className="mt-16 text-center">
          <div className="mx-auto mb-4" style={{ width: 60, height: 1, backgroundColor: GOLD }} />
          <p className="text-[10px] uppercase font-semibold text-sand-600" style={{ letterSpacing: '0.28em' }}>
            Dickies-Campus.de · Wissen, das weiterhilft.
          </p>
          <Link to="/" className="inline-block mt-4 text-[13px] font-semibold hover:underline underline-offset-4" style={{ color: TERRA }} data-testid="accessibility-back-link">
            Zur Anmeldung
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityPage;
